import { Input, Select, SelectProps, Tooltip } from "antd-v5";
import { HelpIcon } from "components/Icons";
import { compact, find, isString, map, pickBy, slice } from "lodash";
import React, { Key, useEffect, useMemo, useRef, useState } from "react";
import { LabelValue } from "types/common";
import {
  FilterChangeCallBack,
  FilterInfo,
  FilterValue,
} from "types/pagination";
import { KeyValue } from "types/tenantConfig";
import { parseSelectOptions } from "utils/formUtils";
import {
  IPv4FilterMessage,
  IPv6FilterMessage,
} from "views/Inventory/Assets/constants";

import ACDurationPicker from "ac-components/ACDurationPicker";
import ACButton from "ac-components/ac-button/ACButton";
import { ACFilterDropdownProps } from "ac-components/ac-filter-bar/ACFilterBar";
import PlusIcon from "ac-components/ac-icons/PlusIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import ACTagFilterField from "ac-components/ac-tag-filter-field/ACTagFilterField";

import "./ACColumnFilterGroup.less";

type Props = Omit<
  ACFilterDropdownProps,
  "items" | "filterInfo" | "onFilterChange"
> & {
  items: LabelValue[];
  filterInfo: FilterInfo;
  onFilterChange: FilterChangeCallBack;
};

type FilterRow = {
  key?: string;
  values?: FilterValue[];
};

export default function ACColumnFilterGroup({
  items,
  filterInfo,
  onFilterChange,
  title,
  visible,
  filterOptionMap = {},
  filterTypeMap = {},
}: Props) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [rows, setRows] = useState<FilterRow[]>();
  const appliedFilterKeys = useMemo(() => {
    const itemKeys = map(items, "value");
    return Object.keys(pickBy(filterInfo, (v, key) => itemKeys.includes(key)));
  }, [filterInfo, items]);

  useEffect(() => {
    if (visible) {
      setRows(
        appliedFilterKeys.map((key) => ({ key, values: filterInfo[key] })),
      );
    }
  }, [visible, appliedFilterKeys, filterInfo]);

  const addRow = () => {
    setRows((prev) => [...(prev || []), {}]);
  };

  const removeFilter = (index: number) => {
    const newRows = slice(rows, 0);
    const oldKey = newRows[index].key;
    newRows.splice(index, 1);
    applyFilter(newRows, { oldKey });
  };

  const onFilterKeyChange = (key: string, index: number) => {
    const newRows = slice(rows, 0);
    const oldKey = newRows[index].key;
    newRows[index].key = key;
    newRows[index].values = [];
    setRows(newRows);
    applyFilter(newRows, { oldKey });
  };

  const onValueChange = (
    values: FilterValue[],
    index: number,
    type: "text" | "select" = "select",
  ) => {
    const newRows = slice(rows, 0);
    newRows[index].values = values;
    setRows(newRows);
    if (type === "text") {
      // timeout for text search as we don't have submit button
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        applyFilter(newRows);
      }, 500);
    } else {
      applyFilter(newRows);
    }
  };

  const applyFilter = (
    rows: FilterRow[],
    { oldKey }: { oldKey?: string } = {},
  ) => {
    const newFiltersInfo = { ...filterInfo };
    if (oldKey) {
      delete newFiltersInfo[oldKey];
    }
    rows.forEach((row) => {
      if (row.key) {
        newFiltersInfo[row.key] = row.values || [];
      }
    });
    onFilterChange(newFiltersInfo);
  };

  const clearFilters = () => {
    const newRows = slice(rows, 0).filter((row) => row.key);
    newRows.forEach((row) => {
      row.values = [];
    });
    setRows(newRows);
    applyFilter(newRows);
  };

  const options: SelectProps["options"] = items.map((item) => ({
    ...item,
    disabled: appliedFilterKeys.includes(item.value),
  }));
  return (
    <div className="column-filter ac-column-filter-group">
      {title && <div className="text-highlight">{title}</div>}
      {map(rows, ({ key, values }, i) => {
        const placeholder = (find(options, { value: key })?.label ??
          "Select attribute") as string;
        return (
          <div className="ac-filter-group-row">
            <div className="ac-filter-group-row-key">
              <Select
                className="w-100"
                options={options}
                optionFilterProp="label"
                value={isString(key) ? key : undefined}
                data-index={i}
                onChange={(newKey) => onFilterKeyChange(newKey, i)}
                placeholder="Attribute"
              />
            </div>
            <div className="ac-filter-group-row-values">
              {!key ? (
                <Input placeholder={placeholder} disabled />
              ) : filterTypeMap[key] === "multiselect" ? (
                <Select<FilterValue[]>
                  className="w-100"
                  options={parseSelectOptions(filterOptionMap[key])}
                  optionFilterProp="label"
                  value={values}
                  mode="multiple"
                  data-index={i}
                  onChange={(newKey) => onValueChange(newKey, i)}
                  placeholder={placeholder}
                  allowClear
                />
              ) : filterTypeMap[key] === "daterange" ? (
                <ACDurationPicker
                  value={values as Key[]}
                  onChange={(values) => onValueChange(values as Key[], i)}
                />
              ) : filterTypeMap[key] === "tag" ? (
                <ACTagFilterField
                  value={values as KeyValue[]}
                  onChange={(value) => onValueChange(value, i)}
                />
              ) : (
                <Input
                  placeholder={placeholder}
                  onChange={(e) =>
                    onValueChange(compact([e.target.value]), i, "text")
                  }
                  value={key ? (values?.[0] as string) : ""}
                  allowClear
                  suffix={
                    filterTypeMap[key] === "ip" ? (
                      <Tooltip
                        title={
                          key === "assetIPV4"
                            ? IPv4FilterMessage
                            : IPv6FilterMessage
                        }
                      >
                        <HelpIcon />
                      </Tooltip>
                    ) : undefined
                  }
                />
              )}
            </div>
            <div className="ac-filter-group-row-delete">
              <ACButton
                iconOnly
                type="outline"
                onClick={() => removeFilter(i)}
                style={{ width: 32, height: 32 }}
              >
                <TrashIcon className="text-danger" />
              </ACButton>
            </div>
          </div>
        );
      })}
      <div className="d-flex justify-content-between w-100">
        <ACButton
          leftIcon={<PlusIcon />}
          size="sm"
          type="outline"
          onClick={addRow}
          disabled={(rows?.length ?? 0) >= items.length}
        >
          Add {title}
        </ACButton>
        <ACButton size="sm" type="outline" onClick={clearFilters}>
          Clear
        </ACButton>
      </div>
    </div>
  );
}
